import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { CTA } from '../components/CTA';
import { Monitor, CheckCircle2, Cloud, FileText, Users, Shield, RefreshCw } from 'lucide-react';

const features = [
  {
    icon: Cloud,
    title: 'Cloud-basiert',
    description: 'Zugriff von überall – im Büro, unterwegs oder im Homeoffice. Keine lokale Installation nötig.'
  },
  {
    icon: FileText,
    title: 'AZAV-konform',
    description: 'Alle Vorlagen und Strukturen entsprechen den Anforderungen der AZAV. Auditfertig dokumentiert.'
  },
  {
    icon: Users,
    title: 'Mehrbenutzer',
    description: 'Arbeiten Sie im Team. Rollen und Berechtigungen lassen sich individuell vergeben.'
  },
  {
    icon: RefreshCw,
    title: 'Automatische Updates',
    description: 'Bei Änderungen der AZAV-Anforderungen aktualisieren wir die Vorlagen automatisch.'
  },
  {
    icon: Shield,
    title: 'Datensicherheit',
    description: 'Hosting in Deutschland, DSGVO-konform. Ihre Daten sind sicher und geschützt.'
  },
  {
    icon: CheckCircle2,
    title: 'Support inklusive',
    description: 'Bei Fragen zur Software oder zum QM-System sind wir für Sie da.'
  }
];

const HandbuchSoftware = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="handbuch-software-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <main className="pt-20 relative">
        {/* Header */}
        <section className="py-24 md:py-32 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 mb-6">
                <Monitor size={18} className="text-primary" />
                <span className="text-sm text-primary font-medium">Digitales QM-System</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none mb-6" data-testid="handbuch-software-headline">
                Digitale{' '}
                <span className="text-primary">Handbuch-Software</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                Ihr QM-Handbuch digital und immer aktuell. AZAV-konforme Strukturen, einfache Bedienung und 
                automatische Updates bei Änderungen der Anforderungen.
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                >
                  Demo anfragen
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 md:py-32 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                Funktionen
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                Alles für Ihr QM-System
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Die Software wurde speziell für Bildungsträger im AZAV-Kontext entwickelt.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card/50 border border-white/10 rounded-2xl p-8 hover:border-primary/30 transition-all"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-sm uppercase tracking-widest text-primary font-semibold mb-4 block">
                  Inhalt
                </span>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6">
                  Was ist enthalten?
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    Die Handbuch-Software enthält alle Dokumente, die Sie für ein AZAV-konformes 
                    Qualitätsmanagementsystem benötigen:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Vollständiges QM-Handbuch mit allen Kapiteln</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Prozessbeschreibungen und Verfahrensanweisungen</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Formulare und Checklisten für den täglichen Betrieb</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Vorlagen für Maßnahmekonzepte und Kalkulationen</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                      <span>Dokumentenlenkung und Versionierung</span>
                    </li>
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-card/30 border border-white/10 rounded-2xl p-8">
                  <h3 className="text-xl font-heading font-semibold text-white mb-6">
                    Für wen geeignet?
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5">
                      <h4 className="font-semibold text-white mb-2">Neue Bildungsträger</h4>
                      <p className="text-sm text-muted-foreground">
                        Starten Sie mit einem fertigen, auditreifen QM-System.
                      </p>
                    </div>
                    <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5">
                      <h4 className="font-semibold text-white mb-2">Bestehende Träger</h4>
                      <p className="text-sm text-muted-foreground">
                        Digitalisieren Sie Ihr QM-System und behalten Sie den Überblick.
                      </p>
                    </div>
                    <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5">
                      <h4 className="font-semibold text-white mb-2">Träger mit mehreren Standorten</h4>
                      <p className="text-sm text-muted-foreground">
                        Einheitliche Dokumentation an allen Standorten.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default HandbuchSoftware;
